'use client'

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { debounce } from 'lodash'

import { VCardOption } from '@/lib/types/VCardT'
import useClickOutside from '@/lib/hooks/useClickOutside'
import { cn } from '@/lib/utils'
import VCardSelectInput from '@/components/VCardSelectField/SelectInput'
import VCardDropdownItem from '@/components/VCardSelectField/DropdownItem'
import Button from '@/components/livecontact/Button'
import { POP_CLASS } from '@/components/livecontact/fieldStyles'
import Spinner from '@/components/livecontact/Spinner'
import LcIcon from '@/components/livecontact/icons'

type VCardSelectFieldProps = {
  placeholder?: string
  onSearch: (search: string) => void
  isFetching: boolean
  emptyText: string
  vCardList: VCardOption[]
  isMultiple?: boolean
  value: VCardOption[]
  onChange: (vCards: VCardOption[]) => void
  // Pagination props
  hasMore?: boolean
  onLoadMore?: () => void
  isLoadingMore?: boolean
  maxDisplay?: number
}

/** Async contact-card picker with infinite scroll + an explicit "Load more" row. */
const VCardSelectField = ({
  placeholder = 'Search contact cards…',
  onChange,
  onSearch,
  value,
  vCardList,
  isFetching,
  isMultiple = false,
  emptyText,
  hasMore = false,
  onLoadMore,
  isLoadingMore = false,
  maxDisplay,
}: VCardSelectFieldProps) => {
  const [inputValue, setInputValue] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const debouncedSearch = useMemo(() => debounce(onSearch, 500), [onSearch])

  const selectedVCards = useMemo(() => value ?? [], [value])

  const handleInputChange = (val: string) => {
    setInputValue(val)
    debouncedSearch(val)
  }

  const handleVCardSelect = (vCard: VCardOption) => {
    if (isMultiple) {
      const exists = selectedVCards.some((v) => v.id === vCard.id)
      const newValue = exists
        ? selectedVCards.filter((v) => v.id !== vCard.id)
        : [...selectedVCards, vCard]
      onChange(newValue)
    } else {
      onChange([vCard])
      setIsFocused(false)
    }
    handleInputChange('')
    inputRef.current?.focus()
  }

  const handleRemoveVCard = (id: number) => {
    if (isMultiple) {
      onChange(selectedVCards.filter((v) => v.id !== id))
    } else {
      onChange([])
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && isMultiple && inputValue === '' && selectedVCards.length > 0) {
      const newValue = selectedVCards.slice(0, -1)
      onChange(newValue)
    }
    if (e.key === 'Escape') setIsFocused(false)
  }

  // Close dropdown when clicking outside
  const handleClickOutside = useCallback(() => {
    setIsFocused(false)
  }, [])

  useClickOutside(wrapperRef, handleClickOutside, isFocused)

  // Handle scroll for pagination
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget
      const scrolledToBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 50 // 50px threshold

      if (scrolledToBottom && hasMore && !isLoadingMore && onLoadMore) {
        onLoadMore()
      }
    },
    [hasMore, isLoadingMore, onLoadMore]
  )

  useEffect(() => {
    return () => debouncedSearch.cancel()
  }, [debouncedSearch])

  const isSearching = inputValue.trim().length > 0

  return (
    <div ref={wrapperRef} className="relative">
      <VCardSelectInput
        selectedVCards={selectedVCards}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onRemoveVCard={handleRemoveVCard}
        onKeyDown={handleKeyDown}
        inputRef={inputRef}
        placeholder={placeholder}
        isMultiple={isMultiple}
        maxDisplay={maxDisplay}
        onFocus={() => setIsFocused(true)}
      />

      {isFocused && (
        <div
          ref={dropdownRef}
          role="listbox"
          aria-multiselectable={isMultiple}
          onScroll={handleScroll}
          className={cn(
            'absolute left-0 right-0 top-full z-50 mt-1.5 max-h-64 origin-top overflow-y-auto animate-lc-pop lc-scrollbar-thin',
            POP_CLASS
          )}
        >
          {isFetching ? (
            <div
              className="flex items-center justify-center gap-2 py-4 text-[13px] text-ink-4"
              role="status"
            >
              <Spinner className="size-3.5" />
              {isSearching ? 'Searching…' : 'Loading…'}
            </div>
          ) : vCardList.length > 0 ? (
            <>
              <div className="flex flex-col gap-0.5">
                {vCardList.map((vCard) => {
                  const isSelected = selectedVCards.some((v) => v.id === vCard.id)
                  return (
                    <VCardDropdownItem
                      key={vCard.id}
                      vCard={vCard}
                      isSelected={isSelected}
                      onSelect={handleVCardSelect}
                    />
                  )
                })}
              </div>
              {isLoadingMore && (
                <div
                  className="mt-1 flex items-center justify-center gap-2 border-t border-line py-2.5 text-[12px] text-ink-4"
                  role="status"
                >
                  <Spinner className="size-3" />
                  Loading more…
                </div>
              )}
              {!isLoadingMore && hasMore && (
                <div className="mt-1 border-t border-line pt-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full text-brand"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => onLoadMore?.()}
                  >
                    Load more
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center gap-1.5 px-3 py-5 text-center">
              <span className="inline-flex size-8 items-center justify-center rounded-[8px] bg-surface-2 text-ink-4 dark:bg-surface-3">
                <LcIcon name={isSearching ? 'search' : 'contact-book'} />
              </span>
              <span className="text-[13px] leading-[1.4] tracking-[-0.2px] text-ink-4">
                {isSearching ? `No cards match “${inputValue.trim()}”.` : emptyText}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default VCardSelectField
